import React, { useState } from 'react';
import { Zap, CheckCircle2, Loader2, AlertCircle, ArrowRight, KeyRound } from 'lucide-react';
import { AuthService, AuthSession, UserSettings } from '../services/authService';
import { LicenseManager } from '../utils/licenseManager';

interface TrialActivationProps {
  session: AuthSession;
  userSettings: UserSettings;
  onTrialActivated: (session: AuthSession) => void;
  onLicenseActivated: (session: AuthSession) => void;
}

export const TrialActivationScreen: React.FC<TrialActivationProps> = ({
  session,
  userSettings,
  onTrialActivated,
  onLicenseActivated
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showLicenseInput, setShowLicenseInput] = useState(false);
  const [licenseKey, setLicenseKey] = useState('');

  const handleActivateTrial = async () => {
    setLoading(true);
    setError('');

    try {
      const machineId = await LicenseManager.getMachineId();
      const platform = await LicenseManager.getPlatform();
      
      const details = {
        name: userSettings.name || '',
        company: userSettings.company || '',
        address: userSettings.address || '',
        email: session.email,
        phone: userSettings.phone || '',
        platform: platform || 'unknown',
        otp: 'REGISTRATION_VERIFIED' // Special flag indicating OTP was already verified during registration
      };
      
      const license = await LicenseManager.activateTrial(machineId, details);
      
      // Update session with trial info
      const updatedSession: AuthSession = {
        ...session,
        licenseNumber: license.key,
        tier: 'Trial',
        validUntil: new Date(license.expiryDate).toISOString()
      };
      
      // Save updated session
      await AuthService.saveSession(updatedSession);
      
      // License is already saved by LicenseManager, just call success callback
      setTimeout(() => {
        onTrialActivated(updatedSession);
      }, 1000);
    } catch (error: any) {
      setError(error.message || 'Failed to activate trial');
    }
    setLoading(false);
  };

  const handleActivateLicense = async () => {
    if (!licenseKey.trim()) {
      setError('Please enter a license key');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const machineId = await LicenseManager.getMachineId();
      const platform = await LicenseManager.getPlatform();

      // Call server to activate/assign dormant license
      const response = await fetch('https://us-central1-plate-license-sys.cloudfunctions.net/activateLicense', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          licenseKey: licenseKey.trim(),
          email: session.email,
          deviceId: machineId,
          platform
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to activate license');
      }

      // Create license info
      const license = {
        key: data.licenseId || licenseKey.trim(),
        hwId: machineId,
        tier: data.tier === 'PROFESSIONAL' || data.tier === 'professional' ? 'PROFESSIONAL' : 'BASIC',
        expiryDate: new Date(data.validUntil || data.expiryDate).getTime(),
        contactName: userSettings.name || session.email,
        companyName: userSettings.company || '',
        isTrial: false
      };

      // Save license locally
      LicenseManager.saveLicense(license as any);

      // Update session
      const updatedSession: AuthSession = {
        ...session,
        licenseNumber: license.key,
        tier: license.tier,
        validUntil: new Date(license.expiryDate).toISOString()
      };

      await AuthService.saveSession(updatedSession);

      setTimeout(() => {
        onLicenseActivated(updatedSession);
      }, 1000);
    } catch (error: any) {
      setError(error.message || 'Failed to activate license');
    }
    setLoading(false);
  };

  if (showLicenseInput) {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-4">
        <div className="w-full max-w-md">
          <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl p-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl mb-4 shadow-lg">
                <KeyRound className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-1">Enter License Key</h1>
              <p className="text-slate-400 text-sm">Activate your purchased license</p>
            </div>

            {/* License Key Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">License Key</label>
              <input
                type="text"
                value={licenseKey}
                onChange={(e) => setLicenseKey(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX-XXXX-XXXX"
                className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 font-mono text-center tracking-wider"
                disabled={loading}
              />
            </div>

            {error && (
              <div className="p-3 bg-red-900/20 border border-red-500/30 rounded-lg flex items-start gap-3 mb-6 animate-in fade-in slide-in-from-top-2">
                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            {/* Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleActivateLicense}
                disabled={loading || !licenseKey.trim()}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-400 hover:to-indigo-500 disabled:from-slate-700 disabled:to-slate-700 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Activating...
                  </>
                ) : (
                  <>
                    Activate License
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <button
                onClick={() => {
                  setShowLicenseInput(false);
                  setError('');
                  setLicenseKey('');
                }}
                disabled={loading}
                className="w-full bg-slate-800 hover:bg-slate-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-slate-300 font-medium py-3 rounded-lg transition-colors"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-4">
      <div className="w-full max-w-md">
        <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl p-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl mb-4 shadow-lg">
              <Zap className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-1">Welcome!</h1>
            <p className="text-slate-400 text-sm">Account created successfully</p>
          </div>

          {/* Message */}
          <div className="mb-8 p-5 bg-blue-900/20 border border-blue-500/30 rounded-lg">
            <p className="text-slate-200 text-center text-sm mb-3">
              Welcome <span className="font-semibold">{userSettings.name}</span> from <span className="font-semibold">{userSettings.company}</span>!
            </p>
            <p className="text-slate-400 text-center text-sm">
              Choose how you'd like to get started with RegIQ.
            </p>
          </div>

          {/* Trial Benefits */}
          <div className="mb-8 space-y-2">
            <p className="text-slate-300 font-semibold text-sm mb-3">7-Day Trial Includes:</p>
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <p className="text-slate-400 text-sm">Full Professional features</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <p className="text-slate-400 text-sm">Unlimited plate designs</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <p className="text-slate-400 text-sm">All advanced tools included</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <p className="text-slate-400 text-sm">No credit card required</p>
              </div>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-900/20 border border-red-500/30 rounded-lg flex items-start gap-3 mb-6 animate-in fade-in slide-in-from-top-2">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleActivateTrial}
              disabled={loading}
              className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-400 hover:to-orange-500 disabled:from-slate-700 disabled:to-slate-700 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Activating...
                </>
              ) : (
                <>
                  Start 7-Day Free Trial
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            <button
              onClick={() => setShowLicenseInput(true)}
              disabled={loading}
              className="w-full bg-slate-800 hover:bg-slate-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-slate-300 font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <KeyRound className="w-4 h-4" />
              I Have a License Key
            </button>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-slate-700 text-center text-xs text-slate-500">
            <p>Your trial expires in 7 days. Purchase a license to continue using RegIQ after the trial.</p>
          </div>
        </div>
      </div>
    </div>
  );
};
